/**
 * Semrush Safe-Fetch Wrapper (Story 20.4, AC #5; Round 4 Red Team Attack #1)
 *
 * Thin shim over the existing `safeFetch`. The shim's job is to redact the
 * literal `key=<value>` query-string parameter from URLs BEFORE any logging,
 * error-reporting, or Sentry breadcrumb emission.
 *
 * Why this is necessary: Semrush sends the API key as a `key=` query
 * parameter, which means the literal key value appears in EVERY request URL.
 * `safeFetch`'s default error path includes the URL hostname in the error
 * message, but error messages from `fetch()` itself (DNS errors,
 * connection-refused, abort-controller timeouts) often re-include the full
 * URL. Without redaction at the boundary, the plaintext key leaks into:
 *
 *   - Cloudflare Workers' built-in request logs.
 *   - Sentry breadcrumbs / error-tracking stacks.
 *   - Our own structured-log events (`semrush_api_call_*`).
 *   - Customer-visible adapter error messages on a 5xx surface.
 *
 * Defense at the boundary > defense at every consumer (which we'd inevitably
 * miss in some code path).
 */

import { safeFetch } from "../safe-fetch";
import type { AdapterResult } from "../adapter";
import { sanitizeSemrushErrorMessage } from "./errors";

/** Per-call timeout (15s — matches Ahrefs). */
const SEMRUSH_TIMEOUT_MS = 15_000;

/**
 * Redact the `key=<value>` query-string parameter from a URL.
 *
 * Returns the URL with `key=[redacted-semrush-key]` substituted in place
 * of any `key=...` segment. Bounded `[^&\s"'<>]{1,2048}` so adversarial
 * inputs cannot trigger backtracking.
 *
 * **Code Review Pass 1 (Critical):** the regex requires a URL-query
 * separator (`?`, `&`) OR start-of-string OR whitespace before `key=` so
 * arbitrary prose like `"foreign key=..."` is not corrupted. Mirrors the
 * tightened pattern in `errors.ts:sanitizeSemrushErrorMessage`.
 *
 * Exported for tests; production consumers should not call this directly —
 * use `safeFetchSemrush` so redaction happens at the call boundary.
 */
export function redactSemrushUrlKey(url: string): string {
  return url.replace(
    /(^|[?&\s])key=[^&\s"'<>]{1,2048}/g,
    "$1key=[redacted-semrush-key]"
  );
}

/**
 * `safeFetch` wrapper that:
 *
 *  1. Executes the underlying fetch via `safeFetch` (retry + timeout).
 *  2. On error, sanitizes the error message via
 *     `sanitizeSemrushErrorMessage` so the literal key is redacted from any
 *     surface that downstream code might log.
 *
 * Pass `decryptedKey` so the second-line-of-defense in
 * `sanitizeSemrushErrorMessage` can also redact the literal key value
 * (defends against the case where the URL form was already mangled).
 *
 * **Important — leak boundary documentation (Code Review Pass 3 audit):**
 * - The actual `fetch(url, init)` MUST receive the un-redacted URL or
 *   Semrush would reject the request. This is unavoidable.
 * - Cloudflare Workers' built-in request logs are outside our control —
 *   the URL appears in those logs by design. The runtime redaction
 *   wrappers DO NOT prevent that.
 * - What we DO control: every structured-log event WE emit
 *   (`semrush_api_call_*`, `semrush_endpoint_warning`, etc.) MUST NOT
 *   include the URL. Audit confirmed: all structured-log emitters in
 *   semrush.ts + route + queue handlers include only IDs / counters /
 *   sanitized error messages — no raw URLs.
 * - The error path through `safeFetch` itself only includes `hostname`
 *   (via `safeHostname`), not the full URL — so DNS / abort / network
 *   errors are bounded. We re-sanitize defensively below in case any
 *   future code path leaks the URL into `error.message`.
 *
 * **Pass 3 invariant:** `init.body` MUST NOT contain the API key. All
 * Semrush helpers in this story use GET with the key as a query parameter;
 * if a future story adds POST endpoints, the body MUST be sanitized
 * separately. The TypeError below catches the most obvious mistake.
 */
export async function safeFetchSemrush(
  url: string,
  decryptedKey: string,
  init?: RequestInit
): Promise<AdapterResult<Response>> {
  // Pass 3 defense — synchronous TypeError if a caller accidentally puts
  // the API key in the body. Cheap check; only trips on actual misuse.
  if (
    decryptedKey.length >= 16 &&
    typeof init?.body === "string" &&
    init.body.includes(decryptedKey)
  ) {
    throw new TypeError(
      "safeFetchSemrush: init.body must not contain the API key. Use the URL query parameter only."
    );
  }
  const result = await safeFetch(url, init, SEMRUSH_TIMEOUT_MS);
  if (result.success) {
    return result;
  }
  // Re-sanitize the error message — `safeFetch`'s built-in
  // `sanitizeErrorMessage` doesn't know about Semrush's `key=` URL pattern.
  return {
    success: false,
    error: {
      ...result.error,
      message: sanitizeSemrushErrorMessage(result.error.message, decryptedKey),
      providerMessage: result.error.providerMessage
        ? sanitizeSemrushErrorMessage(result.error.providerMessage, decryptedKey)
        : undefined,
    },
  };
}
